/**
 * App Version Detail Modal Component
 *
 * Modal for viewing and editing app version details.
 * Provides comprehensive view of version configuration with inline editing.
 */

import { useState, useEffect } from 'react';
import { Edit, Save, Loader2, ExternalLink } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useAppVersionById, useUpdateAppVersion } from '@/hooks/use-app-versions';
import { useToast } from '@/hooks/use-toast';
import type { Priority } from '@/types/app-version';

interface AppVersionDetailModalProps {
  versionId: string | null;
  onClose: () => void;
}

/**
 * Update Form Schema
 */
const updateFormSchema = z.object({
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  minSupportedVersion: z.string().optional().or(z.literal('')),
  forceUpdateAfter: z.string().optional().or(z.literal('')),
  storeUrlIos: z.string().url().optional().or(z.literal('')),
  storeUrlAndroid: z.string().url().optional().or(z.literal('')),
  messageImageUrl: z.string().url().optional().or(z.literal('')),
  messageTitle: z.string().optional().or(z.literal('')),
  messageDescription: z.string().optional().or(z.literal('')),
  mandatoryMessage: z.string().optional().or(z.literal('')),
  actionButtonText: z.string().min(1, 'Required'),
  dismissButtonText: z.string().optional().or(z.literal('')),
  releaseNotes: z.string().optional().or(z.literal('')),
  isMandatory: z.boolean(),
  isActive: z.boolean(),
});

type UpdateFormValues = z.infer<typeof updateFormSchema>;

/**
 * Format Date
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Not set';
  return new Date(dateString).toLocaleString();
}

/**
 * App Version Detail Modal
 * Renders modal with view and edit modes
 */
export function AppVersionDetailModal({ versionId, onClose }: AppVersionDetailModalProps) {
  const { toast } = useToast();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: version, isLoading } = useAppVersionById(versionId);
  const updateAppVersionMutation = useUpdateAppVersion();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<UpdateFormValues>({
    resolver: zodResolver(updateFormSchema),
  });

  const watchedIsMandatory = watch('isMandatory');

  // Initialize form when version loads
  useEffect(() => {
    if (version) {
      reset({
        priority: version.priority,
        minSupportedVersion: version.minSupportedVersion || '',
        forceUpdateAfter: version.forceUpdateAfter
          ? new Date(version.forceUpdateAfter).toISOString().slice(0, 16)
          : '',
        storeUrlIos: version.storeUrlIos || '',
        storeUrlAndroid: version.storeUrlAndroid || '',
        messageImageUrl: version.messageImageUrl || '',
        messageTitle: version.messageTitle || '',
        messageDescription: version.messageDescription || '',
        mandatoryMessage: version.mandatoryMessage || '',
        actionButtonText: version.actionButtonText,
        dismissButtonText: version.dismissButtonText || '',
        releaseNotes: version.releaseNotes || '',
        isMandatory: version.isMandatory,
        isActive: version.isActive,
      });
    }
  }, [version, reset]);

  const onSubmit = async (data: UpdateFormValues) => {
    if (!version) return;

    setIsSubmitting(true);
    try {
      await updateAppVersionMutation.mutateAsync({
        id: version.id,
        data: {
          priority: data.priority,
          minSupportedVersion: data.minSupportedVersion || undefined,
          forceUpdateAfter: data.forceUpdateAfter || undefined,
          storeUrlIos: data.storeUrlIos || undefined,
          storeUrlAndroid: data.storeUrlAndroid || undefined,
          messageImageUrl: data.messageImageUrl || undefined,
          messageTitle: data.messageTitle || undefined,
          messageDescription: data.messageDescription || undefined,
          mandatoryMessage: data.mandatoryMessage || undefined,
          actionButtonText: data.actionButtonText,
          dismissButtonText: data.dismissButtonText || undefined,
          releaseNotes: data.releaseNotes || undefined,
          isMandatory: data.isMandatory,
          isActive: data.isActive,
        },
      });

      toast({
        title: 'Version Updated',
        description: `Version ${version.version} has been updated successfully`,
      });

      setIsEditMode(false);
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update version',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={versionId !== null} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="sr-only">Loading version details</DialogTitle>
            <DialogDescription className="sr-only">
              Loading version details, please wait
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!version) return null;

  return (
    <Dialog open={versionId !== null} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode && <span className="text-primary font-normal">[Editing]</span>}
            Version {version.version}
            <Badge variant={version.isActive ? 'default' : 'secondary'}>
              {version.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {version.isMandatory && <Badge variant="destructive">Mandatory</Badge>}
          </DialogTitle>
          <DialogDescription>
            {version.platform.toUpperCase()} • {version.appBundleId}
          </DialogDescription>
        </DialogHeader>

        {/* Edit button - separated from header to avoid crowding X button */}
        {!isEditMode && (
          <div className="flex justify-end -mt-2">
            <Button size="sm" onClick={() => setIsEditMode(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        )}

        {/* Edit Mode - Form without tabs */}
        {isEditMode ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-4">
            {/* Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={watch('priority')}
                    onValueChange={value => setValue('priority', value as Priority)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minSupportedVersion">Min Supported Version</Label>
                  <Input
                    id="minSupportedVersion"
                    {...register('minSupportedVersion')}
                    placeholder="1.0.0"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="forceUpdateAfter">Force Update After</Label>
                <Input
                  id="forceUpdateAfter"
                  type="datetime-local"
                  {...register('forceUpdateAfter')}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">Force update after this date/time</p>
              </div>
            </div>

            {/* Store URLs */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Store URLs</h3>
              <div className="space-y-2">
                <Label htmlFor="storeUrlIos">iOS Store URL</Label>
                <Input
                  id="storeUrlIos"
                  {...register('storeUrlIos')}
                  placeholder="https://apps.apple.com/..."
                  disabled={isSubmitting}
                />
                {errors.storeUrlIos && (
                  <p className="text-xs text-destructive">{errors.storeUrlIos.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="storeUrlAndroid">Android Store URL</Label>
                <Input
                  id="storeUrlAndroid"
                  {...register('storeUrlAndroid')}
                  placeholder="https://play.google.com/..."
                  disabled={isSubmitting}
                />
                {errors.storeUrlAndroid && (
                  <p className="text-xs text-destructive">{errors.storeUrlAndroid.message}</p>
                )}
              </div>
            </div>

            {/* Messaging */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Messaging</h3>
              <div className="space-y-2">
                <Label htmlFor="messageTitle">Message Title</Label>
                <Input id="messageTitle" {...register('messageTitle')} disabled={isSubmitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="messageDescription">Message Description</Label>
                <Textarea
                  id="messageDescription"
                  {...register('messageDescription')}
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="messageImageUrl">Message Image URL</Label>
                <Input
                  id="messageImageUrl"
                  type="url"
                  {...register('messageImageUrl')}
                  placeholder="https://..."
                  disabled={isSubmitting}
                />
                {errors.messageImageUrl && (
                  <p className="text-xs text-destructive">{errors.messageImageUrl.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="mandatoryMessage">Mandatory Update Message</Label>
                <Textarea
                  id="mandatoryMessage"
                  {...register('mandatoryMessage')}
                  rows={2}
                  placeholder="Message shown when update is mandatory..."
                  disabled={isSubmitting}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="actionButtonText">Action Button</Label>
                  <Input
                    id="actionButtonText"
                    {...register('actionButtonText')}
                    disabled={isSubmitting}
                  />
                  {errors.actionButtonText && (
                    <p className="text-xs text-destructive">{errors.actionButtonText.message}</p>
                  )}
                </div>
                {!watchedIsMandatory && (
                  <div className="space-y-2">
                    <Label htmlFor="dismissButtonText">Dismiss Button</Label>
                    <Input
                      id="dismissButtonText"
                      {...register('dismissButtonText')}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="releaseNotes">Release Notes</Label>
                <Textarea
                  id="releaseNotes"
                  {...register('releaseNotes')}
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Status</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isMandatory"
                    checked={watchedIsMandatory}
                    onCheckedChange={checked => setValue('isMandatory', checked as boolean)}
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="isMandatory" className="text-sm font-normal cursor-pointer">
                    Mandatory update
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isActive"
                    checked={watch('isActive')}
                    onCheckedChange={checked => setValue('isActive', checked as boolean)}
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="isActive" className="text-sm font-normal cursor-pointer">
                    Active
                  </Label>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditMode(false);
                  reset();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          /* View Mode - Show tabs */
          <>
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="metadata">Metadata</TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6 mt-6">
                <div className="space-y-6">
                  {/* View Mode - Configuration */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Configuration</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Priority:</span>
                        <p className="font-medium capitalize">{version.priority}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Min Supported:</span>
                        <p className="font-mono">{version.minSupportedVersion || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* View Mode - Store URLs */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Store URLs</h3>
                    <div className="space-y-2">
                      {version.storeUrlIos && (
                        <div>
                          <span className="text-muted-foreground text-sm">iOS:</span>
                          <a
                            href={version.storeUrlIos}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-primary hover:underline mt-1"
                          >
                            {version.storeUrlIos}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                      {version.storeUrlAndroid && (
                        <div>
                          <span className="text-muted-foreground text-sm">Android:</span>
                          <a
                            href={version.storeUrlAndroid}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-primary hover:underline mt-1"
                          >
                            {version.storeUrlAndroid}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* View Mode - Messaging */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">Messaging</h3>
                    {version.messageTitle && (
                      <div>
                        <span className="text-muted-foreground text-sm">Title:</span>
                        <p className="text-sm mt-1">{version.messageTitle}</p>
                      </div>
                    )}
                    {version.messageDescription && (
                      <div>
                        <span className="text-muted-foreground text-sm">Description:</span>
                        <p className="text-sm mt-1">{version.messageDescription}</p>
                      </div>
                    )}
                    {version.releaseNotes && (
                      <div>
                        <span className="text-muted-foreground text-sm">Release Notes:</span>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{version.releaseNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Metadata Tab */}
              <TabsContent value="metadata" className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Version ID:</span>
                    <p className="font-mono text-xs mt-1">{version.id}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Bundle ID:</span>
                    <p className="font-mono text-xs mt-1">{version.appBundleId}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Platform:</span>
                    <p className="font-medium capitalize mt-1">{version.platform}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Beta Version:</span>
                    <p className="mt-1">{version.isBeta ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Force Update After:</span>
                    <p className="text-xs mt-1">{formatDate(version.forceUpdateAfter)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Release Date:</span>
                    <p className="text-xs mt-1">{formatDate(version.releaseDate)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="text-xs mt-1">{formatDate(version.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated:</span>
                    <p className="text-xs mt-1">{formatDate(version.updatedAt)}</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* View Mode Footer - Close button */}
            <div className="flex justify-end pt-4 border-t mt-4">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
